import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { BankFeedsService } from 'src/app/data/service/bank-feeds.service';
import { DroppedTransactionRow, ImportRun, ImportRunTransaction, importTriggerLabel } from 'src/app/data/model/bank-feed';

/**
 * SD-1207: the import history, as the History tab of the import wizard.
 *
 * Every import that has ever written to dbo.UnreconciledReport funnels through one
 * method on the server, so this covers the Stripe bank feed, the Xero pull and CSV
 * uploads alike -- which is the point. Before this, the per-import outcome was returned
 * to the browser once and then discarded, so "why is this account short forty
 * transactions in March" had no answer but to read the rows.
 *
 * It lives in the import drawer, so it is a list rather than a table: a run per row,
 * opening in place to show what it did. Server-paged, like WorkflowHistoryComponent: a
 * busy organisation accumulates a run per feed per day, so the list grows without bound
 * and cannot be filtered in memory.
 */
@Component({
  selector: 'app-import-history',
  templateUrl: './import-history.component.html',
  styleUrls: ['./import-history.component.scss']
})
export class ImportHistoryComponent implements OnInit, OnDestroy {

  @Input() organisationId: string;

  runs: ImportRun[] = [];
  loading = false;
  error = '';

  searchText = '';
  readonly pageSize = 10;
  /** 1-based, as the API counts. */
  page = 1;
  total = 0;

  /** The run opened in place, if any. */
  selectedId: string = null;
  selectedDropped: DroppedTransactionRow[] = [];

  /** The open run's transactions, loaded when it is opened. */
  selectedTransactions: ImportRunTransaction[] = [];
  selectedTransactionTotal = 0;
  transactionsLoading = false;
  transactionsError = '';
  private transactionsSub: Subscription | null = null;

  private searchChanged = new Subject<string>();
  private subscription = new Subscription();

  constructor(private readonly _bankFeedsService: BankFeedsService) { }

  ngOnInit(): void {
    this.subscription.add(
      this.searchChanged
        .pipe(debounceTime(300), distinctUntilChanged())
        .subscribe(search => {
          this.searchText = search;
          // A new term changes the result set, so the old page means nothing.
          this.page = 1;
          this.load();
        })
    );
    this.load();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.clearTransactions();
  }

  load(): void {
    if (!this.organisationId) { return; }

    this.loading = true;
    this.error = '';

    this._bankFeedsService
      .getImports(this.organisationId, this.page, this.pageSize, this.searchText)
      .subscribe(
        page => {
          this.runs = page.runs || [];
          this.total = page.total || 0;
          this.loading = false;
        },
        () => {
          this.loading = false;
          this.error = 'We could not load your import history.';
        }
      );
  }

  onSearchInput(value: string): void {
    this.searchChanged.next((value || '').trim());
  }

  refresh(): void {
    this.load();
  }

  get pageCount(): number {
    return Math.max(1, Math.ceil(this.total / this.pageSize));
  }

  /** "11–20 of 34". */
  get rangeLabel(): string {
    if (!this.total) { return ''; }
    const first = (this.page - 1) * this.pageSize + 1;
    const last = Math.min(this.page * this.pageSize, this.total);
    return `${first}–${last} of ${this.total}`;
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.pageCount || page === this.page || this.loading) { return; }
    this.page = page;
    this.selectedId = null;
    this.clearTransactions();
    this.load();
  }

  /**
   * Opens a run in place, or closes it. The dropped rows are stored as JSON on the run --
   * a capped sample, because a dropped row exists to be read, not queried.
   */
  toggle(run: ImportRun): void {
    if (this.selectedId === run.id) {
      this.selectedId = null;
      this.selectedDropped = [];
      this.clearTransactions();
      return;
    }

    this.selectedId = run.id;
    this.selectedDropped = [];
    this.loadTransactions(run);

    if (run.droppedSampleJson) {
      try {
        this.selectedDropped = JSON.parse(run.droppedSampleJson) || [];
      } catch {
        // Bad JSON must not take the list down; the counts are the authoritative record.
        this.selectedDropped = [];
      }
    }
  }

  /**
   * Which transactions the run imported, changed or withdrew. Fetched on opening: a run can
   * hold thousands, and most runs are never opened.
   */
  private loadTransactions(run: ImportRun): void {
    this.clearTransactions();
    if (run.status === 'Running' || run.status === 'Failed') { return; }

    this.transactionsLoading = true;
    this.transactionsSub = this._bankFeedsService.getImportTransactions(this.organisationId, run.id).subscribe(
      list => {
        if (this.selectedId !== run.id) { return; }
        this.selectedTransactions = list.transactions;
        this.selectedTransactionTotal = list.total;
        this.transactionsLoading = false;
      },
      () => {
        this.transactionsLoading = false;
        this.transactionsError = 'We could not load the transactions for this import.';
      });
  }

  private clearTransactions(): void {
    if (this.transactionsSub) { this.transactionsSub.unsubscribe(); this.transactionsSub = null; }
    this.selectedTransactions = [];
    this.selectedTransactionTotal = 0;
    this.transactionsLoading = false;
    this.transactionsError = '';
  }

  /** A run that changed something but has no transactions recorded predates the recording. */
  recordedBefore(run: ImportRun): boolean {
    return !this.transactionsLoading && !this.transactionsError && !this.selectedTransactions.length
      && !!(run.insertedCount || run.updatedCount || run.voidedCount || run.conflictCount);
  }

  /** What the run did to a transaction, in words. */
  actionLabel(tx: ImportRunTransaction): string {
    switch (tx && tx.action) {
      case 'Inserted': return 'Imported';
      case 'Updated': return 'Updated';
      case 'Voided': return 'Withdrawn';
      case 'Conflict': return 'Left as is';
      case 'Reconciled': return 'Marked reconciled';
      case 'Unreconciled': return 'Back to reconcile';
      case 'Removed': return 'Removed';
      default: return '';
    }
  }

  actionTitle(tx: ImportRunTransaction): string {
    switch (tx && tx.action) {
      case 'Updated': return 'Your bank changed this transaction, and the change was applied.';
      case 'Voided': return 'Your bank withdrew this transaction, so it was removed from Transactions.';
      case 'Conflict': return 'Your bank changed this transaction, but it was already reconciled, so it was not changed.';
      case 'Reconciled': return 'Reconciled in Xero, so it was marked reconciled here.';
      case 'Unreconciled': return 'No longer reconciled in Xero, so it went back to reconcile here.';
      case 'Removed': return 'No longer in Xero, so it was removed from Transactions.';
      default: return 'Imported by this run.';
    }
  }

  /** Where it stands now: reconciled, still to reconcile, or no longer in Transactions. */
  nowLabel(tx: ImportRunTransaction): string {
    if (!tx.inLedger) { return tx.action === 'Voided' || tx.action === 'Removed' ? '' : 'No longer in Transactions'; }
    return tx.isReconciled ? 'Reconciled' : 'To reconcile';
  }

  trackByTransaction(index: number, tx: ImportRunTransaction): string {
    return (tx.unreconciledReportId || '') + ':' + tx.action + ':' + index;
  }

  trackById(_: number, run: ImportRun): string {
    return run.id;
  }

  /** What an import did, as a sentence rather than seven columns of numbers. */
  summary(run: ImportRun): string {
    if (!run) { return ''; }
    if (run.status === 'Failed') { return run.errorMessage || 'Failed.'; }
    if (run.status === 'Running') { return 'In progress...'; }

    const parts: string[] = [];
    if (run.insertedCount) { parts.push(`${run.insertedCount} imported`); }
    // A Refresh's updates are status changes, and what it takes out is gone from Xero, not withdrawn by a bank.
    const refresh = run.trigger === 'Refresh';
    if (run.updatedCount) { parts.push(`${run.updatedCount} ${refresh ? 'status changed' : 'updated'}`); }
    if (run.duplicateCount) { parts.push(`${run.duplicateCount} already here`); }
    if (run.voidedCount) { parts.push(`${run.voidedCount} ${refresh ? 'removed' : 'withdrawn'}`); }
    if (run.conflictCount) { parts.push(`${run.conflictCount} reconciled already`); }
    if (run.unmappedCount) { parts.push(`${run.unmappedCount} no account`); }

    return parts.length ? parts.join(', ') : 'Nothing new';
  }

  /** Where the rows came from, in the words a user would use. */
  sourceLabel(run: ImportRun): string {
    switch (run && run.source) {
      case 'StripeFeed': return 'Bank feed';
      case 'Xero': return 'Xero';
      case 'Spreadsheet': return 'Spreadsheet';
      case 'ChromeExtension': return 'Browser extension';
      case 'API': return 'API';
      default: return 'Import';
    }
  }

  /**
   * Which account, or which file. Xero and spreadsheet imports can span accounts, so
   * "All accounts" is a real answer rather than a missing value.
   */
  targetLabel(run: ImportRun): string {
    if (!run) { return ''; }
    return run.accountName || run.fileName || 'All accounts';
  }

  /**
   * What set the import off. Only shown when it was not a person -- "Manual" next to a
   * run somebody obviously started by hand is noise.
   */
  triggerLabel(run: ImportRun): string {
    // Shared vocabulary, so a run reads the same here and in Settings > Banking.
    if (!run || run.trigger === 'Manual') { return ''; }
    return importTriggerLabel(run);
  }

  statusClass(run: ImportRun): string {
    switch (run && run.status) {
      case 'Success': return 'ih-chip--ok';
      case 'Partial': return 'ih-chip--partial';
      case 'Failed': return 'ih-chip--failed';
      default: return 'ih-chip--running';
    }
  }

  /**
   * Stored values are UTC without a zone marker, so they have to be marked as UTC before
   * the date pipe gets them or they render an hour or eleven out. Same helper, same
   * reason, as WorkflowHistoryComponent.
   */
  localDate(value: string): Date {
    if (!value) { return null; }
    const text = value.endsWith('Z') || value.includes('+') ? value : value + 'Z';
    const parsed = new Date(text);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  /**
   * A transaction's date is a calendar day, not an instant: read as UTC (as localDate does) it
   * would show a day early west of Greenwich. Built from its parts, in local time.
   */
  txDate(value: string | null): Date | null {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value || '');
    return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
  }

  /** The reason a row was dropped, in words. */
  dropReason(row: DroppedTransactionRow): string {
    switch (row && row.reason) {
      case 'duplicate': return 'Already imported';
      case 'invalid_account': return 'No matching account';
      default: return 'Could not import';
    }
  }
}
